<script>
/* global axios */
import { mapActions, mapState, mapGetters } from 'vuex';
import LoadingState from '../../../../components/widgets/LoadingState.vue';
import CrawlLimitProgressBar from './CrawlLimitProgressBar.vue';
import { useAlert } from 'dashboard/composables';

export default{
  name: "FireCrawl",
  computed: {
    ...mapState('knowledge', ['crawlResult', 'isUploaded']),
    ...mapGetters(['getCurrentAccountId']),
    isLimited(){
      return this.firecrawlUsage?.limit < this.firecrawlUsage?.used;
    },
    proxyBase() { return `/api/v1/accounts/${this.getCurrentAccountId}/proxy/knowledge`; },
    rawCrawlResult(){
      return this.crawlResult;
    }
  },
  components: {
    LoadingState,
    CrawlLimitProgressBar,
  },
  watch: {
    rawCrawlResult(){
      this.fetchUsage();
    },
    isLoading(newVal){
      this.$emit('isCrawling', newVal)
    }
  },
  data(){
    return{
      url: null,
      limit: 10,
      maxDepth: 2,
      useSiteMap: true,
      onlyMainContent: true,
      includedPath: "",
      excludedPath: "",
      isLoading: false,
      selectedResult: [],
      isCrawled: false,
      firecrawlUsage: { used: 0, limit: 0 },
      isLoadingComponent: true,
    }
  },
  methods: {
    ...mapActions(
      'knowledge', ['handleCrawl', 'handleRemoveFile']
    ),
    emitScrollDown(){
      this.$emit('scrollDown')
    },
    async waitForCondition(conditionFn, interval = 100, maxAttempts = 100) {
      return new Promise((resolve, reject) => {
        let attempts = 0;
        const check = setInterval(() => {
          attempts += 1;
          if (conditionFn()) {
            clearInterval(check);
            resolve();
          } else if (attempts >= maxAttempts) {
            clearInterval(check);
            reject(new Error('Timed out waiting for crawl to finish.'));
          }
        }, interval);
      });
    },
    async startCrawl() {
      if (!this.url) {
        useAlert(this.$t('KNOWLEDGE.FIRECRAWL.INVALID_URL_ERROR'));
        return;
      }
      if(this.isUploaded){
        this.handleRemoveFile();
      }
      this.isLoading = true;
      try {
        await this.handleCrawl({
          url: this.url,
          limit: this.limit,
          maxDepth: this.maxDepth,
          useSiteMap: this.useSiteMap,
          onlyMainContent: this.onlyMainContent,
          includedPath: this.includedPath,
          excludedPath: this.excludedPath,
        });
        this.isCrawled = true;
        // handleCrawl's promise resolves as soon as the first status poll
        // fires, well before a still-"scraping" crawl actually finishes —
        // wait here (bounded, so a failed/stuck crawl can't hang forever).
        await this.waitForCondition(() => this.isUploaded === true, 500, 600);
      } catch (error) {
        console.error('Crawl failed:', error);
        useAlert(this.$t('KNOWLEDGE.FIRECRAWL.CRAWL_ERROR'));
      } finally {
        this.isLoading = false;
      }
    },
    async fetchUsage() {
      try{
        const { data: response } = await axios.get(
          `${this.proxyBase}/crawl/${this.getCurrentAccountId}/usage`
        );
        this.firecrawlUsage = response;
      }catch(err){
        useAlert(err);
        console.error(err);
      }
    },
  },
  async mounted(){
    this.isLoadingComponent = true;
    await this.fetchUsage();
    this.isLoadingComponent = false;
  }
}
</script>

<template>
<div>
  <div v-if="isLoadingComponent" class="my-4 p-6 rounded-md bg-slate-200 dark:bg-slate-800 h-[500px] w-full flex items-center justify-center">
    <LoadingState />
  </div>
  <div v-if="!isLoadingComponent" class="my-4 p-6 rounded-md bg-slate-200 dark:bg-slate-800">
    <CrawlLimitProgressBar :usedCrawl="firecrawlUsage.used" :crawlLimit="firecrawlUsage.limit" />
    <br />
    <span class="flex gap-2">
      <input
        id="url-input"
        class="p-0 m-0"
        type="text"
        v-model="url"        
        :placeholder="$t('KNOWLEDGE.FIRECRAWL.URL_PLACEHOLDER')"
        :aria-label="$t('KNOWLEDGE.FIRECRAWL.URL_ARIA_LABEL')"
        @change="isCrawled = false"
      />
      <woot-button 
        @click="startCrawl"
        v-if="!isLoading"
        :disabled="isCrawled || isLimited"
      >
      {{ $t('KNOWLEDGE.FIRECRAWL.NEXT_BUTTON') }}
      </woot-button>
      <woot-button v-else class="flex items-center justify-center">
        <svg class="animate-spin h-5 w-5 text-gray-500" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          ></circle>
          <circle
            class="opacity-75"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
            stroke-dasharray="80"
            stroke-dashoffset="60"
          ></circle>
        </svg>
      </woot-button>
    </span>
    <div v-if="isCrawled" class="flex items-center gap-2">
      <span class="spinner" v-if="isLoading" />
      <h1>
        <span class="font-bold text-woot-500" :class="{'animate-pulse': isLoading}">
          {{ rawCrawlResult.data.length || 0 }}
        </span>
        results extracted
      </h1>
      <div @click="emitScrollDown">
        <fluent-icon icon="chevron-down" class="cursor-pointer rounded text-white bg-woot-500 hover:bg-woot-700"/>
      </div>
    </div>
    <hr>
    <h1>{{ $t('KNOWLEDGE.FIRECRAWL.OPTIONS_TITLE') }}</h1>
    <br>
    <label class="flex gap-2 items-center">
      <input type="checkbox" v-model="useSiteMap" />
      <p class="m-0 p-0">{{ $t('KNOWLEDGE.FIRECRAWL.CRAWL_SUBPAGES') }}</p>
    </label>
    <br>
    <div class="grid grid-cols-2 grid-rows-2 gap-2">      
      <label class="flex flex-col">
        <p>{{ $t('KNOWLEDGE.FIRECRAWL.LIMIT_LABEL') }}</p>
        <input type="number" v-model="limit" />
      </label>
      <label class="flex flex-col">
        <p>{{ $t('KNOWLEDGE.FIRECRAWL.MAX_DEPTH_LABEL') }}</p>
        <input type="number" v-model="maxDepth" />
      </label>
      <label class="flex flex-col">
        <p>{{ $t('KNOWLEDGE.FIRECRAWL.EXCLUDED_PATHS_LABEL') }}</p>
        <input type="text" :placeholder="$t('KNOWLEDGE.FIRECRAWL.EXCLUDED_PATHS_PLACEHOLDER')" v-model="excludedPath" />
      </label>
      <label class="flex flex-col">
        <p>{{ $t('KNOWLEDGE.FIRECRAWL.INCLUDED_PATHS_LABEL') }}</p>
        <input type="text" :placeholder="$t('KNOWLEDGE.FIRECRAWL.INCLUDED_PATHS_PLACEHOLDER')" v-model="includedPath" />
      </label>
    </div>    
    <label class="flex gap-2 items-center">
      <input type="checkbox" v-model="onlyMainContent" />
      <p class="m-0 p-0">{{ $t('KNOWLEDGE.FIRECRAWL.MAIN_CONTENT_ONLY') }}</p>
    </label>
  </div>
</div>

</template>